import axios, {AxiosError} from "axios";
import {get, isString} from "lodash";
import {useCallback, useEffect} from "react";
import {toast} from "sonner";
import {useAmplitude} from "../contexts/AmplitudeContext";
import {useAuth} from "../hooks/useAuth";
import {axiosPartnerPlatformInstance} from "./ServerApiManager";

const ServerApiExceptionManager = () => {
  const {setError} = useAmplitude();

  const notificationHandler = useCallback(
    (error: AxiosError) => {
      const errorData = get(error, "response.data");
      const errorMessage = errorData && isString(errorData) ? errorData : undefined;
      setError(errorMessage);
      let displayMessage;

      if (error.response) {
        // Request was made and server responded with an error
        displayMessage = errorMessage || "An error has occurred";
      } else if (error.request) {
        // Request was made but no response received (network error)
        displayMessage = "Network error: Unable to connect to the server";
      } else {
        // Error occurred while setting up the request
        displayMessage = "An error has occurred. Please try again";
      }

      toast.error(displayMessage);
    },
    [setError]
  );

  const {handleSignOut} = useAuth();

  const errorHandler = useCallback(
    (error: AxiosError) => {
      const errorStatus = get(error, "response.status");
      const signOut = async (reload: boolean) => {
        await handleSignOut();
        if (reload) {
          window.location.reload();
        }
      };

      if (errorStatus === 401) {
        signOut(true);
        return Promise.resolve();
      }
      if (errorStatus === 403) {
        toast.error(String(error.response?.data) || "An error has occurred");
        signOut(false);
        return Promise.resolve();
      }

      const isCancel = axios.isCancel(error);
      if (isCancel) {
        return Promise.reject(error);
      }

      notificationHandler(error);
      return Promise.resolve();
    },
    [handleSignOut, notificationHandler]
  );

  useEffect(() => {
    const interceptorId = axiosPartnerPlatformInstance.interceptors.response.use(undefined, errorHandler);
    return () => {
      axiosPartnerPlatformInstance.interceptors.response.eject(interceptorId);
    };
  }, [errorHandler]);

  return null;
};

export default ServerApiExceptionManager;
